import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { BACKEND_URL } from "@/lib/api";
import { cr, crErr } from "@/lib/cricketApi";
import { Stage, themeVars, CricketFonts, img } from "@/components/cricket/graphics";

// 1920×1080 broadcast canvas scaled to fit its container (preview, website and outputs share it).
export function ScaledStage({ ctx, mode, chroma, editable, onLayout, W = 1920, H = 1080 }) {
  const ref = useRef(null);
  const [k, setK] = useState(0.3);
  useEffect(() => {
    const el = ref.current; if (!el) return undefined;
    const ro = new ResizeObserver(() => setK(Math.min(el.clientWidth / W, (el.clientHeight || el.clientWidth * H / W) / H)));
    ro.observe(el); return () => ro.disconnect();
  }, [W, H]);
  return (
    <div ref={ref} className="relative h-full w-full overflow-hidden">
      <div style={{ width: W, height: H, transform: `scale(${k})`, transformOrigin: "top left" }}><Stage ctx={ctx} mode={mode} chroma={chroma} editable={editable} k={k} onLayout={onLayout} /></div>
    </div>
  );
}

// Moment alert to show now: manual (onair.moment) or auto-triggered from published moments.
export function activeMoment(data, seenRef) {
  const on = data?.onair || {}; const cfg = data?.tournament?.moments || {};
  if (on.moment?.at && Date.now() - new Date(on.moment.at).getTime() < (cfg[on.moment.type]?.duration || 4) * 1000) return on.moment;
  const ms = data?.live?.state?.moments || []; const last = ms[ms.length - 1];
  if (!last || !cfg[last.type]?.enabled || !cfg[last.type]?.auto) return null;
  if (seenRef.current.seq !== last.seq) seenRef.current = { seq: last.seq, at: Date.now(), first: seenRef.current.seq === undefined };
  if (seenRef.current.first) return null; // don't replay an old alert on page load
  return Date.now() - seenRef.current.at < (cfg[last.type].duration || 4) * 1000 ? last : null;
}

// Fetch once, then refetch the instant the server pushes an update over the websocket (5 s poll as a safety net).
export function useLiveOutput(slug, k) {
  const [data, setData] = useState(null); const [err, setErr] = useState(null); const [live, setLive] = useState(false);
  const load = useRef(null);
  load.current = () => cr.pubOutput(slug, k).then((d) => { setData(d); setErr(null); }).catch((e) => setErr(e?.response?.status || "offline"));
  useEffect(() => {
    let ws; let off = false; let retry; let ping;
    const connect = () => {
      ws = new WebSocket(`${BACKEND_URL.replace(/^http/, "ws")}/api/cricket/ws/${slug}`);
      ws.onopen = () => { setLive(true); load.current(); ping = setInterval(() => ws.readyState === 1 && ws.send("ping"), 25000); };
      ws.onmessage = () => load.current();
      ws.onclose = () => { setLive(false); clearInterval(ping); if (!off) retry = setTimeout(connect, 2000); };
    };
    load.current(); connect();
    const iv = setInterval(() => load.current(), 5000);
    return () => { off = true; clearInterval(iv); clearInterval(ping); clearTimeout(retry); ws?.close(); };
  }, [slug, k]);
  return { data, err, live, reload: () => load.current() };
}

// Live drag/resize of on-air graphics; saved layout is pushed to every output instantly.
export function useLayoutEditor(tid, onair, reload) {
  const [local, setLocal] = useState({});
  const onLayout = async (slot, box, final) => {
    setLocal((x) => ({ ...x, [slot]: box }));
    if (!final) return;
    try { await cr.onair(tid, { layout: { ...(onair?.layout || {}), ...local, [slot]: box } }); await reload?.(); setLocal({}); }
    catch (e) { toast.error(crErr(e)); }
  };
  const reset = async () => { try { await cr.onair(tid, { layout: {} }); await reload?.(); setLocal({}); toast.success("Layout reset to default"); } catch (e) { toast.error(crErr(e)); } };
  return { layout: { ...(onair?.layout || {}), ...local }, onLayout, reset };
}

const outCtx = (data, moment) => ({ tournament: data.tournament, onair: data.onair, teams: data.teams, players: data.players, state: data.live?.state, match: data.live?.match, profile: data.profile, profiles: data.profiles, code: data.code, codes: data.codes, points: data.points, published_at: data.live?.published_at, moment });

function useTick(ms) { const [, f] = useState(0); useEffect(() => { const iv = setInterval(() => f((x) => x + 1), ms); return () => clearInterval(iv); }, [ms]); }

// /cricket/t/:slug/output/:mode — clean overlay for OBS (green | transparent | fullscreen | lower-third | bug)
export function CricketOutput() {
  const { slug, mode } = useParams();
  const k = new URLSearchParams(window.location.search).get("k");
  const { data, err, reload } = useLiveOutput(slug, k);
  const seen = useRef({});
  const [edit, setEdit] = useState(false); const [hover, setHover] = useState(false);
  const hoverT = useRef(null);
  useTick(500);
  const lay = useLayoutEditor(data?.tournament?.id, data?.onair, reload);
  useEffect(() => { document.documentElement.style.background = "transparent"; document.body.style.background = "transparent"; }, []);
  if (!data) return err ? <div className="p-6 text-white">Output unavailable ({String(err)})</div> : null;
  const ctx = outCtx({ ...data, onair: { ...data.onair, layout: lay.layout } }, activeMoment(data, seen));
  const wake = () => { setHover(true); clearTimeout(hoverT.current); hoverT.current = setTimeout(() => setHover(false), 2500); };
  return (
    <div className="fixed inset-0" onMouseMove={data.can_edit ? wake : undefined} data-testid="cricket-output">
      <ScaledStage ctx={ctx} mode={mode} editable={edit} onLayout={lay.onLayout} />
      {data.can_edit && (hover || edit) && (
        <div className="fixed right-3 top-3 z-50 flex gap-2 rounded-full bg-black/80 p-1.5 text-xs font-bold text-white shadow-xl" data-testid="output-edit-bar">
          <button onClick={() => setEdit(!edit)} className={`rounded-full px-3 py-1.5 ${edit ? "bg-[#FFC233] text-black" : "bg-white/10"}`} data-testid="output-edit-toggle">{edit ? "Done adjusting" : "Adjust layout"}</button>
          {edit && <button onClick={lay.reset} className="rounded-full bg-white/10 px-3 py-1.5" data-testid="output-layout-reset">Reset</button>}
        </div>)}
      {edit && <div className="fixed bottom-3 left-1/2 z-50 -translate-x-1/2 rounded-full bg-black/80 px-4 py-2 text-xs text-white" data-testid="output-edit-hint">Drag to move · right edge = stretch wide · bottom edge = stretch tall · corner = resize. Saves live to all outputs.</div>}
    </div>
  );
}

// /cricket/t/:slug — streamer's live website: shows exactly what's on air in the control room, nothing else.
export default function CricketPublic() {
  const { slug } = useParams();
  const { data, err, live } = useLiveOutput(slug, null);
  const seen = useRef({});
  const [narrow, setNarrow] = useState(window.innerWidth < 768);
  useEffect(() => { const f = () => setNarrow(window.innerWidth < 768); window.addEventListener("resize", f); return () => window.removeEventListener("resize", f); }, []);
  useTick(500);
  if (err && !data) return <div className="flex min-h-screen items-center justify-center bg-[#050B1A] text-white" data-testid="cricket-public-error">{err === 403 ? "This tournament is private." : err === 404 ? "Tournament not found." : "Can't reach the server."}</div>;
  if (!data) return <div className="min-h-screen bg-[#050B1A]" />;
  const t = data.tournament; const on = data.onair || {};
  const ctx = outCtx(narrow ? { ...data, onair: { ...on, layout: {} } } : data, activeMoment(data, seen));
  const nothing = !on.fullscreen?.visible && !on.lower_third?.visible && !(on.bug && data.live?.state);
  return (
    <div className="cg flex min-h-screen flex-col" style={{ ...themeVars(t.theme), background: "var(--c-bg)" }} data-testid="cricket-public">
      <CricketFonts />
      <header className="flex items-center gap-3 px-4 py-3 sm:px-8" style={{ background: "var(--c-grad)" }}>
        {t.logo_url && <img src={img(t.logo_url)} alt="" className="h-9 w-9 rounded object-contain" />}
        <div className="min-w-0"><div className="text-xs font-bold uppercase tracking-widest opacity-80">{t.brand_name}</div><h1 className="truncate text-2xl font-black uppercase">{t.name}</h1></div>
        <span className={`ml-auto flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold uppercase ${live ? "bg-red-600 text-white" : "bg-white/10 opacity-70"}`} data-testid="cp-live-indicator"><span className={`h-2 w-2 rounded-full ${live ? "animate-pulse bg-white" : "bg-white/50"}`} /> {live ? "Live" : "Connecting"}</span>
      </header>
      <main className="flex flex-1 items-center justify-center p-2 sm:p-4">
        <div className="relative w-full" style={narrow ? { aspectRatio: "1240 / 1400" } : { aspectRatio: "16 / 9", maxWidth: "min(100%, calc((100vh - 90px) * 16 / 9))" }} data-testid="cp-stage">
          <ScaledStage ctx={ctx} mode="website" W={narrow ? 1240 : 1920} H={narrow ? 1400 : 1080} />
          {nothing && <div className="absolute inset-0 flex items-center justify-center text-2xl font-bold opacity-50" data-testid="cp-nothing">Nothing on air right now</div>}
        </div>
      </main>
    </div>
  );
}
